
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RunFinancialModelButton } from './RunFinancialModelButton';
import { EnhancedAIModelButton } from './EnhancedAIModelButton';
import { 
  AlertTriangle,
  CheckCircle,
  FileSpreadsheet,
  Calculator,
  Zap,
  Brain,
  Target
} from 'lucide-react';

interface DashboardQuickActionsProps {
  hasBalanceSheetData: boolean;
  hasFinancialAssumptions: boolean;
  canRunModel: boolean;
  onModelGenerated: () => void;
  onViewAIDashboard: () => void;
}

export function DashboardQuickActions({ 
  hasBalanceSheetData,
  hasFinancialAssumptions,
  canRunModel,
  onModelGenerated,
  onViewAIDashboard
}: DashboardQuickActionsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Financial Tools
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" className="w-full justify-start">
            NPV Calculator
          </Button>
          <Button variant="outline" className="w-full justify-start">
            Cash Flow Analysis
          </Button>
          <Button variant="outline" className="w-full justify-start">
            Resource Valuation
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            AI Financial Modeling
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm">
              Data: {hasBalanceSheetData ? 'Balance Sheet ✓' : ''} 
              {hasBalanceSheetData && hasFinancialAssumptions ? ', ' : ''}
              {hasFinancialAssumptions ? 'Assumptions ✓' : ''}
              {!canRunModel ? 'No data available' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {canRunModel ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm">Ready for AI analysis</span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <span className="text-sm">Add data to enable AI modeling</span>
              </>
            )}
          </div>
          <RunFinancialModelButton onModelGenerated={onModelGenerated} />
          <Button variant="outline" className="w-full justify-start" onClick={onViewAIDashboard}>
            <Zap className="h-4 w-4 mr-2" />
            View AI Dashboard
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Pre-Production Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <EnhancedAIModelButton onModelGenerated={onModelGenerated} />
        </CardContent>
      </Card>

      <Card className="md:col-span-2 lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-sm">
            <p className="font-medium">Balance Sheet Updated</p>
            <p className="text-gray-600">2 hours ago</p>
          </div>
          <div className="text-sm">
            <p className="font-medium">Financial Forecast Generated</p>
            <p className="text-gray-600">1 day ago</p>
          </div>
          <div className="text-sm">
            <p className="font-medium">New Tenement Added</p>
            <p className="text-gray-600">3 days ago</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
